import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { errorMessage } from '../../lib/api';
import { formatMoney } from '../../lib/format';
import { pipelinesApi } from '../../lib/resources';
import type { Pipeline, Stage, StageSummary } from '../../types';
import { Button, ColorPalette, ErrorNote, Field, Modal, inputClass } from '../ui';

const DEFAULT_STAGES = ['Novo', 'Em negociação', 'Fechamento'];

/** "+ Adicionar pipeline": nome, grupo e as etapas iniciais (uma por linha). */
export function NewPipelineModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (pipeline: Pipeline) => void;
}) {
  const [name, setName] = useState('');
  const [group, setGroup] = useState('');
  const [description, setDescription] = useState('');
  const [stages, setStages] = useState(DEFAULT_STAGES.join('\n'));
  const groups = useQuery({ queryKey: ['pipelines', 'groups'], queryFn: pipelinesApi.groups });

  const stageNames = stages
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const create = useMutation({
    mutationFn: () =>
      pipelinesApi.create({
        name: name.trim(),
        group: group.trim() || undefined,
        description: description.trim() || undefined,
        stages: stageNames.map((stage) => ({ name: stage })),
      }),
    onSuccess: (pipeline) => {
      onCreated(pipeline);
      onClose();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) create.mutate();
  };

  return (
    <Modal title="Nova pipeline" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nome *">
          <input
            autoFocus
            className={inputClass}
            value={name}
            maxLength={160}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Grupo" hint="Agrupa pipelines na barra lateral">
          <input
            className={inputClass}
            list="pipeline-groups"
            value={group}
            maxLength={120}
            onChange={(event) => setGroup(event.target.value)}
          />
          <datalist id="pipeline-groups">
            {(groups.data ?? []).map((item) => (
              <option key={item} value={item} />
            ))}
          </datalist>
        </Field>
        <Field label="Descrição">
          <textarea
            rows={2}
            className={inputClass}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
        <Field label="Etapas" hint={`Uma por linha, na ordem do Kanban (máximo de 25).`}>
          <textarea
            rows={5}
            className={inputClass}
            value={stages}
            onChange={(event) => setStages(event.target.value)}
          />
        </Field>
        {stageNames.length > 25 && (
          <ErrorNote>Uma pipeline pode ter no máximo 25 etapas.</ErrorNote>
        )}
        {create.error && <ErrorNote>{errorMessage(create.error, 'Falha ao criar')}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            type="submit"
            variant="primary"
            disabled={!name.trim() || stageNames.length > 25 || create.isPending}
          >
            Criar pipeline
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * "+ Nova coluna" e "Editar etapa": o nome é pedido antes de criar (decisão do
 * plano), com a cor da coluna.
 */
export function StageFormModal({
  pipelineId,
  stage,
  onClose,
  onSaved,
}: {
  pipelineId: string;
  stage?: Stage;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(stage?.name ?? '');
  const [color, setColor] = useState<string | null>(stage?.color ?? null);

  const save = useMutation({
    mutationFn: () =>
      stage
        ? pipelinesApi.updateStage(stage.id, { name: name.trim(), color: color ?? undefined })
        : pipelinesApi.createStage(pipelineId, {
            name: name.trim(),
            color: color ?? undefined,
          }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Modal title={stage ? 'Editar etapa' : 'Nova coluna'} onClose={onClose}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim()) save.mutate();
        }}
        className="space-y-4"
      >
        <Field label="Nome da etapa *">
          <input
            autoFocus
            className={inputClass}
            value={name}
            maxLength={160}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field label="Cor">
          <ColorPalette value={color} onChange={setColor} allowEmpty />
        </Field>
        {save.error && (
          <ErrorNote>{errorMessage(save.error, 'Falha ao salvar a etapa')}</ErrorNote>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={!name.trim() || save.isPending}>
            {stage ? 'Salvar' : 'Criar coluna'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Excluir etapa (Q15): com negócios, pede a etapa que vai recebê-los. */
export function DeleteStageModal({
  stage,
  stages,
  summary,
  onClose,
  onDeleted,
}: {
  stage: Stage;
  stages: Stage[];
  summary?: StageSummary;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const others = stages.filter((item) => item.id !== stage.id);
  const [moveTo, setMoveTo] = useState(others[0]?.id ?? '');
  const remove = useMutation({
    mutationFn: () => pipelinesApi.removeStage(stage.id, moveTo || undefined),
    onSuccess: () => {
      onDeleted();
      onClose();
    },
  });

  return (
    <Modal title={`Excluir etapa "${stage.name}"`} onClose={onClose}>
      <div className="space-y-4 text-sm text-slate-600">
        <p>
          A etapa vai para a Lixeira e pode ser restaurada em até 30 dias.
          {summary && summary.businessesCount > 0 && (
            <>
              {' '}
              Ela tem <strong>{summary.businessesCount}</strong> negócio(s) em andamento (
              {formatMoney(summary.total)}).
            </>
          )}
        </p>
        <Field
          label="Mover os negócios da etapa para *"
          hint="Vale para todos os negócios da etapa, ganhos e perdidos inclusive."
        >
          <select
            className={inputClass}
            value={moveTo}
            onChange={(event) => setMoveTo(event.target.value)}
          >
            {others.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </Field>
        {remove.error && (
          <ErrorNote>{errorMessage(remove.error, 'Falha ao excluir a etapa')}</ErrorNote>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="danger"
            disabled={remove.isPending || others.length === 0}
            onClick={() => remove.mutate()}
          >
            Mover e excluir etapa
          </Button>
        </div>
      </div>
    </Modal>
  );
}
